import { RECORD_SECTION_KEYS } from '../types/consultation-record.js';
import { getCheckedStateFromModifications } from '../protocol/checked-state.js';
export const RECORD_SECTION_TITLES = {
    ficha_identificacion: 'Ficha de identificación',
    motivo_consulta: 'Motivo de consulta',
    antecedentes: 'Antecedentes',
    enfermedad_actual: 'Enfermedad actual',
    examen_fisico: 'Examen físico',
    evolucion: 'Evolución',
    resultados_estudios: 'Resultados de estudios',
    diagnosticos: 'Diagnósticos',
    plan_tratamiento: 'Plan de tratamiento',
    enmiendas: 'Enmiendas',
};
const REQUIRED_BY_KIND = {
    first_visit: ['motivo_consulta', 'antecedentes', 'examen_fisico', 'diagnosticos', 'plan_tratamiento'],
    evolution: ['motivo_consulta', 'examen_fisico', 'evolucion', 'diagnosticos', 'plan_tratamiento'],
};
/** Sections that never render for a given kind (spec §5 table "—" cells). */
const EXCLUDED_BY_KIND = {
    first_visit: ['evolucion'],
    evolution: ['enfermedad_actual'],
};
function normalize(text) {
    return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
}
/** Free-narrative destination: evolución on follow-ups, enfermedad actual on first visits. */
function narrativeSection(kind) {
    return kind === 'first_visit' ? 'enfermedad_actual' : 'evolucion';
}
/** Resolves any destination, routing to the narrative default if the section is excluded for this kind. */
function resolveSection(destination, kind) {
    const excluded = EXCLUDED_BY_KIND[kind];
    return excluded.includes(destination) ? narrativeSection(kind) : destination;
}
function matchNotesSection(label, kind) {
    const n = normalize(label);
    if (n.includes('motivo'))
        return 'motivo_consulta';
    if (n.includes('antecedente'))
        return 'antecedentes';
    if (n.includes('examen') || n.includes('fisic') || n.includes('exploracion'))
        return 'examen_fisico';
    if (n.includes('diagnostic'))
        return 'diagnosticos';
    if (n.includes('plan') || n.includes('tratamiento'))
        return 'plan_tratamiento';
    if (n.includes('evolucion'))
        return 'evolucion';
    if (n.includes('resultado') || n.includes('estudio'))
        return 'resultados_estudios';
    return narrativeSection(kind);
}
function calcAgeYears(dateOfBirth) {
    if (!dateOfBirth)
        return null;
    const ms = Date.now() - new Date(dateOfBirth).getTime();
    return Math.floor(ms / (365.25 * 24 * 60 * 60 * 1000));
}
const SEX_LABELS = { male: 'Masculino', female: 'Femenino', other: 'Otro' };
function buildFicha(patient) {
    const lines = [];
    const age = calcAgeYears(patient.dateOfBirth);
    const idBits = [
        `${patient.firstName} ${patient.lastName}`.trim(),
        age != null ? `${age} años` : null,
        patient.sex ? (SEX_LABELS[patient.sex] ?? patient.sex) : null,
    ].filter(Boolean);
    lines.push(idBits.join(' · '));
    if (patient.documentNumber) {
        lines.push(`${(patient.documentType ?? 'doc').toUpperCase()}: ${patient.documentNumber}`);
    }
    if (patient.phone)
        lines.push(`Teléfono: ${patient.phone}`);
    if (patient.address)
        lines.push(`Dirección: ${patient.address}`);
    if (patient.allergies.length > 0)
        lines.push(`Alergias: ${patient.allergies.join(', ')}`);
    if (patient.chronicConditions.length > 0) {
        lines.push(`Condiciones crónicas: ${patient.chronicConditions.join(', ')}`);
    }
    return lines.join('\n');
}
function push(bucket, key, text) {
    if (!text.trim())
        return;
    const arr = bucket.get(key) ?? [];
    arr.push(text.trim());
    bucket.set(key, arr);
}
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
function walkBlocks(blocks, usage, kind, bucket, checkedState) {
    for (const raw of blocks) {
        const block = raw;
        const mapping = usage.historiaMapping?.[block.id];
        if (mapping?.include === false)
            continue;
        switch (block.type) {
            case 'section':
                walkBlocks((block.blocks ?? []), usage, kind, bucket, checkedState);
                break;
            case 'clinical_notes': {
                const content = String(block.content ?? '');
                if (content.trim()) {
                    const baseDestination = mapping?.section ?? matchNotesSection(mapping?.label ?? String(block.label ?? ''), kind);
                    const destination = resolveSection(baseDestination, kind);
                    push(bucket, destination, content);
                }
                break;
            }
            case 'vitals': {
                const values = (block.values ?? {});
                const fields = (block.fields ?? []);
                const parts = fields
                    .filter((f) => values[f.id] !== undefined && values[f.id] !== '')
                    .map((f) => `${f.label} ${String(values[f.id])}${f.unit ? ` ${f.unit}` : ''}`);
                if (parts.length > 0) {
                    const baseDestination = mapping?.section ?? 'examen_fisico';
                    const destination = resolveSection(baseDestination, kind);
                    const text = mapping?.label ? `${mapping.label}: ${parts.join(' · ')}` : parts.join(' · ');
                    push(bucket, destination, text);
                }
                break;
            }
            case 'checklist': {
                const items = (block.items ?? []);
                const checked = items
                    .filter((i) => (i.id in checkedState ? checkedState[i.id] === true : i.checked === true))
                    .map((i) => i.text);
                if (checked.length > 0) {
                    const baseDestination = mapping?.section ?? narrativeSection(kind);
                    const destination = resolveSection(baseDestination, kind);
                    const title = mapping?.label ?? String(block.title ?? 'Verificación');
                    push(bucket, destination, `${title}: ${checked.join(', ')}`);
                }
                break;
            }
            case 'steps': {
                const steps = (block.steps ?? []);
                const completedIds = new Set((usage.modifications.steps_completed ?? []).map((s) => s.step_id));
                const skipped = new Map((usage.modifications.steps_skipped ?? []).map((s) => [s.step_id, s.reason]));
                const parts = [];
                for (const step of steps) {
                    if (completedIds.has(step.id))
                        parts.push(step.title);
                    else if (skipped.has(step.id)) {
                        const reason = skipped.get(step.id);
                        parts.push(`${step.title} (omitido${reason ? `: ${reason}` : ''})`);
                    }
                }
                if (parts.length > 0) {
                    const baseDestination = mapping?.section ?? narrativeSection(kind);
                    const destination = resolveSection(baseDestination, kind);
                    const title = mapping?.label ?? String(block.title ?? 'Pasos');
                    push(bucket, destination, `${title}: ${parts.join(' · ')}`);
                }
                break;
            }
            case 'decision': {
                const chosen = (usage.modifications.decision_branches ?? []).find((d) => d.decision_id === block.id);
                if (chosen) {
                    const branches = (block.branches ?? []);
                    const label = branches.find((b) => b.id === chosen.branch_id)?.label ?? '';
                    if (label) {
                        const baseDestination = mapping?.section ?? narrativeSection(kind);
                        const destination = resolveSection(baseDestination, kind);
                        const prefix = mapping?.label ?? 'Decisión';
                        push(bucket, destination, `${prefix}: ${String(block.condition ?? '')} → ${label}`);
                    }
                }
                break;
            }
            // dosage_table / lab_order / imaging_order: plan comes from order records.
            // alert / text: reference material, never part of the historia.
            default:
                break;
        }
    }
}
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
/** Off-protocol notes flow into the narrative section for their consultation kind, timestamps stripped. */
function pushOffProtocolNotes(usage, kind, bucket) {
    const destination = narrativeSection(kind);
    for (const note of usage.modifications.off_protocol_notes ?? []) {
        const text = note.title ? `${note.title}: ${note.note}` : note.note;
        push(bucket, destination, text);
    }
}
function buildPlan(orders) {
    const lines = [];
    for (const item of orders.prescriptionItems) {
        const duration = item.duration ? ` — ${item.duration}` : '';
        lines.push(`${item.drug} ${item.dose} ${item.route} ${item.frequency}${duration}`);
    }
    if (orders.labTests.length > 0)
        lines.push(`Laboratorio: ${orders.labTests.join(', ')}`);
    if (orders.imagingStudies.length > 0)
        lines.push(`Imágenes: ${orders.imagingStudies.join(', ')}`);
    return lines.join('\n');
}
export function generateRecordSections(input) {
    const bucket = new Map();
    push(bucket, 'ficha_identificacion', buildFicha(input.patient));
    for (const usage of input.usages) {
        const checkedState = getCheckedStateFromModifications(usage.modifications);
        walkBlocks(usage.blocks, usage, input.kind, bucket, checkedState);
        pushOffProtocolNotes(usage, input.kind, bucket);
    }
    push(bucket, 'plan_tratamiento', buildPlan(input.orders));
    for (const amendment of input.amendments) {
        push(bucket, 'enmiendas', `${amendment.amendedAt.slice(0, 10)}: ${amendment.reason}`);
    }
    const required = new Set(REQUIRED_BY_KIND[input.kind]);
    const excluded = new Set(EXCLUDED_BY_KIND[input.kind]);
    const sections = [];
    for (const key of RECORD_SECTION_KEYS) {
        if (excluded.has(key))
            continue;
        // enmiendas only renders when amendments exist; all other kind-valid
        // sections (including empty optional ones) always render so they can be
        // filled in later via the edit UI.
        if (key === 'enmiendas' && input.amendments.length === 0)
            continue;
        const content = (bucket.get(key) ?? []).join('\n\n');
        const isRequired = required.has(key);
        sections.push({
            key,
            title: RECORD_SECTION_TITLES[key],
            content,
            source: 'generated',
            required: isRequired,
        });
    }
    return sections;
}
//# sourceMappingURL=generate-record-sections.js.map